import {
  DEFAULT_SOLO_TIER_ID,
  DEFAULT_TEAM_TIER_ID,
  DEFAULT_UNSUBSCRIBED_SOLO_TIER_ID,
  DEFAULT_UNSUBSCRIBED_TEAM_TIER_ID,
  buildPricingTierCatalogFromPlanComparison,
  getPricingTier as getCatalogPricingTier,
  getPricingTierIntegrationConnections,
  getPricingTierLimits,
  getPricingTierPrice,
  getPricingTiers,
  isCustomPricingTierLimit,
  isUnlimitedPricingTierLimit,
  SOLO_PRICING_TIERS as CATALOG_SOLO_PRICING_TIERS,
  TEAM_PRICING_TIERS as CATALOG_TEAM_PRICING_TIERS,
  type EstimatedMonthlyOperations,
  type PricingTierAction,
  type PricingTierCatalog,
  type PricingTierConnectionLimit,
  type PricingTierGroup,
  type PricingTierId,
  type PricingTierLimit,
  type PricingTierOption as CatalogPricingTierOption,
  type PricingTierStoreLimit,
} from '@common/config/pricingCatalog';
import { isSoloPlanCode, isTeamPlanCode } from './teamOfferSelection';

export type FrontendPricingTierActionLabel = 'Switch plan' | 'Switch to Solo' | 'Switch to Team';
export type PricingTierOption = CatalogPricingTierOption;

export const SOLO_PRICING_TIERS: readonly PricingTierOption[] = CATALOG_SOLO_PRICING_TIERS;
export const TEAM_PRICING_TIERS: readonly PricingTierOption[] = CATALOG_TEAM_PRICING_TIERS;

export {
  DEFAULT_SOLO_TIER_ID,
  DEFAULT_TEAM_TIER_ID,
  DEFAULT_UNSUBSCRIBED_SOLO_TIER_ID,
  DEFAULT_UNSUBSCRIBED_TEAM_TIER_ID,
  getPricingTierIntegrationConnections,
  getPricingTierLimits,
  getPricingTierPrice,
  getPricingTiers,
  isCustomPricingTierLimit,
  isUnlimitedPricingTierLimit,
  buildPricingTierCatalogFromPlanComparison,
};

export type {
  EstimatedMonthlyOperations,
  PricingTierAction,
  PricingTierCatalog,
  PricingTierConnectionLimit,
  PricingTierGroup,
  PricingTierId,
  PricingTierLimit,
  PricingTierStoreLimit,
};

export const getPricingTier = (
  group: PricingTierGroup,
  tierId: PricingTierId,
): PricingTierOption => getCatalogPricingTier(group, tierId);

export const getPricingTierForPlanCode = (
  group: PricingTierGroup,
  planCode?: string | null,
): PricingTierOption | null => {
  const normalizedPlanCode = String(planCode || '').toUpperCase();
  return getPricingTiers(group).find((tier) => tier.planCode === normalizedPlanCode) ?? null;
};

export const resolveSelectedSoloTierId = ({
  entryDefaultTierId,
  hasManualSelection,
  selectedTierId,
  currentPlanCode,
}: {
  entryDefaultTierId?: PricingTierId | null;
  hasManualSelection: boolean;
  selectedTierId: PricingTierId;
  currentPlanCode?: string | null;
}): PricingTierId => {
  if (entryDefaultTierId && !hasManualSelection) return entryDefaultTierId;
  if (hasManualSelection) return selectedTierId;
  return getPricingTierForPlanCode('solo', currentPlanCode)?.id ?? selectedTierId;
};

export const resolveFrontendPricingTierActionLabel = (
  tier: PricingTierOption,
  currentPlanCode?: string | null,
): FrontendPricingTierActionLabel => {
  const normalizedCurrentPlanCode = String(currentPlanCode || '').toUpperCase();
  const isCurrentPlanGroup = tier.group === 'team'
    ? isTeamPlanCode(normalizedCurrentPlanCode)
    : isSoloPlanCode(normalizedCurrentPlanCode);

  if (isCurrentPlanGroup) return 'Switch plan';
  return tier.group === 'team' ? 'Switch to Team' : 'Switch to Solo';
};

export const resolvePricingTierWorkUnits = (
  tier: { workUnits: number | null },
  configuredWorkUnits: number | null,
): number | null => {
  if (configuredWorkUnits !== null && configuredWorkUnits > 0) return configuredWorkUnits;
  return tier.workUnits !== null ? tier.workUnits : null;
};

const WORK_UNIT_DISPLAY_MICROUSD = 100_000;

const toPositiveNumberOrNull = (value: unknown): number | null => {
  if (value == null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const toEstimatedMonthlyOperationsOrNull = (
  value: unknown,
): EstimatedMonthlyOperations | null => {
  if (!value || typeof value !== 'object') return null;

  const candidate = value as { min?: unknown; max?: unknown };
  const min = toPositiveNumberOrNull(candidate.min);
  const max = toPositiveNumberOrNull(candidate.max);

  if (min === null || max === null || min > max) return null;
  return { min: Math.floor(min), max: Math.floor(max) };
};

export const resolveConfiguredPlanWorkUnits = (plan?: {
  displayBudgetMicrousdMonthly?: number | string | null;
  workUnitsBudgetDisplayMicrousdMonthly?: number | string | null;
  pointsQuota?: number | string | null;
} | null): number | null => {
  const displayBudgetMicrousdMonthly = toPositiveNumberOrNull(plan?.displayBudgetMicrousdMonthly);
  if (displayBudgetMicrousdMonthly !== null) {
    return Math.floor(displayBudgetMicrousdMonthly / WORK_UNIT_DISPLAY_MICROUSD);
  }

  const workUnitsBudgetDisplayMicrousdMonthly = toPositiveNumberOrNull(plan?.workUnitsBudgetDisplayMicrousdMonthly);
  if (workUnitsBudgetDisplayMicrousdMonthly !== null) {
    return Math.floor(workUnitsBudgetDisplayMicrousdMonthly / WORK_UNIT_DISPLAY_MICROUSD);
  }

  return toPositiveNumberOrNull(plan?.pointsQuota);
};

export const resolveConfiguredPlanEstimatedOperations = (plan?: {
  estimatedMonthlyOperations?: unknown;
  rules?: Record<string, unknown> | null;
} | null): EstimatedMonthlyOperations | null => (
  toEstimatedMonthlyOperationsOrNull(plan?.estimatedMonthlyOperations)
  ?? toEstimatedMonthlyOperationsOrNull(plan?.rules?.estimatedMonthlyOperations)
);
